// 챗봇 위젯 위치·크기 — 플로팅 버튼/헤더 드래그 이동 + 모서리 리사이즈, 사용자별 localStorage 저장.
//  - 위치 = 화면 우/하단에서의 거리(r,b). 패널은 버튼 근처에 열리고, 여유 공간이 큰 쪽(위/아래, 좌/우)으로 펼쳐진다.
//  - 폭 < 600px 에서는 비활성(기존 모바일 동작 그대로).
import { useState, useRef, useEffect } from 'react';

var MIN_W = 320, MIN_H = 420, FAB_W = 132, FAB_H = 44, NARROW = 600, DRAG_PX = 5, GAP = 60, EDGE = 16;
// r 20 = 오른쪽 아래 모서리 — 오류 신고 떠 있는 버튼이 상단 바로 옮겨 가 비켜설 필요가 없다 (10/21)
export var DEFAULT_LAYOUT = { r: 20, b: 20, w: 400, h: 560 };

function isNarrow() { return window.innerWidth < NARROW; }
// 최소(lo)보다 가용(hi)이 작으면 가용 우선 — 패널이 화면 밖으로 나가지 않게
function between(v, lo, hi) { return Math.max(0, Math.min(Math.max(v, lo), hi)); }

// 버튼 위치 기준 패널 펼침 방향·가용 크기
function geom(l) {
  var vw = window.innerWidth, vh = window.innerHeight;
  var right = (vw - l.r - FAB_W / 2) >= vw / 2;   // 버튼이 오른쪽 절반 → 패널은 왼쪽으로 펼침
  var upRoom = vh - l.b - GAP - 8, downRoom = l.b - 24;
  var up = upRoom >= downRoom;
  return { right: right, up: up, maxW: Math.min(vw - 32, right ? vw - l.r - EDGE : l.r + FAB_H - EDGE), maxH: up ? upRoom : downRoom };
}

// 화면 안으로 클램프 (위치 → 크기 순)
function fitLayout(l) {
  var p = { r: between(l.r, 0, window.innerWidth - FAB_W), b: between(l.b, 0, window.innerHeight - FAB_H), w: l.w, h: l.h };
  var g = geom(p);
  p.w = between(l.w, MIN_W, g.maxW); p.h = between(l.h, MIN_H, g.maxH);
  return p;
}

function loadLayout(key) {
  try {
    var o = JSON.parse(localStorage.getItem(key));
    if (!o || ['r', 'b', 'w', 'h'].some(function (k) { return typeof o[k] !== 'number' || !isFinite(o[k]); })) return null;
    return fitLayout(o);
  } catch (_e) { return null; }
}
function saveLayout(key, l) { try { localStorage.setItem(key, JSON.stringify(l)); } catch (_e) {} }

function trackDrag(e, onMove, onEnd) {
  var sx = e.clientX, sy = e.clientY;
  var move = function (ev) { onMove(ev.clientX - sx, ev.clientY - sy); };
  var up = function () {
    window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); window.removeEventListener('pointercancel', up);
    onEnd();
  };
  window.addEventListener('pointermove', move); window.addEventListener('pointerup', up); window.addEventListener('pointercancel', up);
}

export function useAssistantLayout(key) {
  var _l = useState(function () { return loadLayout(key); }); var lay = _l[0]; var setLay = _l[1];
  var _n = useState(isNarrow); var narrow = _n[0]; var setNarrow = _n[1];
  var moved = useRef(false);

  useEffect(function () {
    var onResize = function () { setNarrow(isNarrow()); setLay(function (p) { return p ? fitLayout(p) : p; }); };
    window.addEventListener('resize', onResize);
    return function () { window.removeEventListener('resize', onResize); };
  }, []);

  // mode: 'fab' | 'head' (위치 이동) | 'size' (크기 조절)
  var begin = function (e, mode) {
    if (narrow || (e.button != null && e.button > 0)) return;
    if (mode === 'head' && e.target.closest && e.target.closest('button')) return;
    e.preventDefault();   // 드래그 중 텍스트 선택 방지
    if (mode === 'fab') moved.current = false;
    var s = lay || DEFAULT_LAYOUT;
    if (!lay && mode !== 'fab') {   // 기본 상태의 첫 조작 — 실제 렌더 크기에서 시작(점프 방지)
      var pn = e.currentTarget.closest && e.currentTarget.closest('.aw-panel'), rc = pn && pn.getBoundingClientRect();
      if (rc) s = { r: s.r, b: s.b, w: Math.round(rc.width) || s.w, h: Math.round(rc.height) || s.h };
    }
    var g = geom(s), live = s;
    trackDrag(e, function (dx, dy) {
      if (mode === 'size') live = fitLayout({ r: s.r, b: s.b, w: s.w + (g.right ? -dx : dx), h: s.h + (g.up ? -dy : dy) });
      else {
        if (mode === 'fab' && !moved.current) { if (Math.sqrt(dx * dx + dy * dy) < DRAG_PX) return; moved.current = true; }
        live = fitLayout({ r: s.r - dx, b: s.b - dy, w: s.w, h: s.h });
      }
      setLay(live);
    }, function () { if (live !== s) saveLayout(key, live); });
  };

  var g = geom(lay || DEFAULT_LAYOUT);
  return {
    enabled: !narrow,
    rootStyle: lay && !narrow ? { right: lay.r, bottom: lay.b } : undefined,
    panelStyle: lay && !narrow ? {
      width: lay.w, height: lay.h, maxWidth: 'none', maxHeight: 'none',
      right: g.right ? 0 : 'auto', left: g.right ? 'auto' : 0, bottom: g.up ? GAP : 'auto', top: g.up ? 'auto' : GAP
    } : undefined,
    grip: (g.up ? 't' : 'b') + (g.right ? 'l' : 'r'),   // 앵커 반대쪽 모서리
    onFab: function (e) { begin(e, 'fab'); },
    onHead: function (e) { begin(e, 'head'); },
    onGrip: function (e) { e.stopPropagation(); begin(e, 'size'); },
    // 드래그였다면 클릭(열기/닫기)을 삼킨다
    click: function (fn) { return function () { if (moved.current) { moved.current = false; return; } fn(); }; },
    reset: function () { try { localStorage.removeItem(key); } catch (_e) {} setLay(null); }
  };
}
